import type { FrameArt } from "../assets/manifest.ts";
import type { PropInstance, TileRef } from "../document/types.ts";
import type { CellRect } from "../terrain/match.ts";
import { GROUND_ROW, groundRow, type OrderedProp } from "./sort.ts";

/**
 * Prop geometry for the editor (ADR-025): a prop's image is anchored at the bottom left of its anchor cell
 * and extends up and right, so selection hit-tests the opaque pixels of every sprite covering the point and
 * placement centres the sprite's columns on the pointer cell.
 */

/**
 * Pixel size of a frame, its base row and, when the manifest carries them, its opaque bounds, its `ground` flag
 * and its `amongProps` mark, read only beside `ground` (ADR-040's amendment of 2026-10-06).
 */
export interface FrameSize {
  readonly w: number;
  readonly h: number;
  readonly base: number;
  readonly art?: FrameArt;
  readonly ground?: true;
  readonly amongProps?: true;
}

/**
 * A world pixel box, every edge inclusive.
 */
export interface PixelBox {
  readonly x0: number;
  readonly y0: number;
  readonly x1: number;
  readonly y1: number;
}

/**
 * Looks up a frame's pixel size and base row.
 */
export type FrameSizeOf = (tileset: string, tile: number) => FrameSize;

/**
 * A prop's base line in world pixels (ADR-040): the anchor cell's bottom edge less the transparent rows between
 * the art's base and the frame bottom, the line the prop sorts by before its sort offset moves it. A vertically
 * flipped prop's is the frame's bottom edge, since its base then sits at the top.
 *
 * @param prop The prop's anchor row and vertical flip.
 * @param size Its frame size and base.
 * @param tileSize Tile size in pixels.
 * @returns The line.
 */
export function propBaseLine(prop: Pick<PropInstance, "y" | "flipY">, size: FrameSize, tileSize: number): number {
  const lift = prop.flipY ? 0 : size.h - size.base;
  return (prop.y + 1) * tileSize - lift;
}

/**
 * Whether a frame lies flat among props: flagged `ground` and marked `amongProps`, which is the only place the mark
 * is read (ADR-040's amendment of 2026-10-06, decision 6).
 *
 * @param size The frame.
 * @returns True when the frame sorts by the top edge of its art.
 */
export function isFlatFrame(size: Pick<FrameSize, "ground" | "amongProps">): boolean {
  return size.ground === true && size.amongProps === true;
}

/**
 * How far above the anchor cell's bottom edge the top of a flat frame's art stands: `h - art.y` for an upright or
 * horizontally mirrored prop, `base` for a vertically mirrored one, whose art then hangs from the frame's top,
 * and `h` for a frame with no `art`.
 *
 * @param prop The prop's vertical flip.
 * @param size Its frame size, base and art.
 * @returns The lift in pixels.
 */
export function propFlatLift(prop: Pick<PropInstance, "flipY">, size: FrameSize): number {
  return size.art === undefined ? size.h : prop.flipY ? size.base : size.h - size.art.y;
}

/**
 * The line a flat frame sorts by, before its sort offset (ADR-040's amendment of 2026-10-06, decision 2): the top
 * row of its art as drawn, the anchor cell's bottom edge less `propFlatLift`.
 *
 * @param prop The prop's anchor row and vertical flip.
 * @param size Its frame size, base and art.
 * @param tileSize Tile size in pixels.
 * @returns The line in world pixels.
 */
export function propFlatLine(prop: Pick<PropInstance, "y" | "flipY">, size: FrameSize, tileSize: number): number {
  return (prop.y + 1) * tileSize - propFlatLift(prop, size);
}

/**
 * The row a prop sorts by (ADR-040, ADR-071 decision 2): the `groundRow` of its ground line, the anchor
 * cell's bottom edge less the transparent rows between the art's base and the frame bottom, plus its sort
 * offset, whole pixels as a fraction of a row (ADR-098 decision 5), so a character whose feet line is the
 * prop's ground line moved by the offset has the prop's row exactly. A vertically
 * flipped prop sorts by the frame's bottom edge, since its base then sits at the top. A flat frame (`isFlatFrame`)
 * sorts by the top edge of its art instead (`propFlatLine`), with the same offset.
 *
 * @param prop The prop.
 * @param size Its frame size and base.
 * @param tileSize Tile size in pixels.
 * @returns The sort row, fractional.
 */
export function propSortRow<P extends Pick<PropInstance, "y" | "flipY" | "sortPixels">>(prop: P, size: FrameSize, tileSize: number): number {
  const line = isFlatFrame(size) ? propFlatLine(prop, size, tileSize) : propBaseLine(prop, size, tileSize);
  return groundRow(line, tileSize) + prop.sortPixels / tileSize;
}

/**
 * The row a prop is drawn at (ADR-040's amendment of 2026-10-06, decisions 4 and 5): `GROUND_ROW` for a frame flagged
 * `ground` and not marked `amongProps`, which lies under every prop and every character, and `propSortRow` for every
 * other frame, a flat one included, whose row is the top of its art. The renderer and the map pictures read this one
 * function, so the editor, the game and the pictures draw the same order.
 *
 * @param prop The prop.
 * @param size Its frame size, base, flags and art.
 * @param tileSize Tile size in pixels.
 * @returns The row, fractional, before the prop's tie key.
 */
export function propDrawRow<P extends Pick<PropInstance, "y" | "flipY" | "sortPixels">>(prop: P, size: FrameSize, tileSize: number): number {
  return size.ground === true && size.amongProps !== true ? GROUND_ROW : propSortRow(prop, size, tileSize);
}

/**
 * The cells a prop draws over.
 *
 * @param prop The prop.
 * @param sizeOf Frame size lookup.
 * @param tileSize Tile size in pixels.
 * @returns Cell rectangle, `x0` and `y0` inclusive, `x1` and `y1` exclusive.
 */
export function propFootprint(prop: PropInstance, sizeOf: FrameSizeOf, tileSize: number): CellRect {
  const size = sizeOf(prop.frame.tileset, prop.frame.tile);
  const right = prop.x + Math.max(1, size.w / tileSize);
  const top = prop.y + 1 - Math.max(1, size.h / tileSize);
  return { x0: Math.floor(prop.x), y0: Math.floor(top), x1: Math.ceil(right), y1: Math.ceil(prop.y + 1) };
}

/**
 * Reports whether a frame pixel is opaque. `px` and `py` are frame-local, unflipped.
 */
export type OpaqueAt = (tileset: string, tile: number, px: number, py: number) => boolean;

/**
 * How a hit-test sees one layer's props (P1.6-T41). `frameOf` is the frame the map draws a prop with,
 * such as a roof previewed open, so a click tests the pixels on screen; the prop's own frame when absent.
 * Props are still ranked by their saved frame's sort row, which is what the map draws their depth from.
 * `pickable` passes over the props it rejects, as if they were not there; every prop is pickable when
 * absent.
 */
export interface PropPick {
  readonly frameOf?: (prop: PropInstance) => TileRef;
  readonly pickable?: (prop: PropInstance) => boolean;
}

/**
 * The prop under a world pixel, ignoring transparent pixels so props behind a larger one stay reachable,
 * and preferring the one drawn on top: the greatest sort row, then, at an exact tie, the greatest tie key
 * (P1.6-T42), the index in layer order standing in for a prop that carries none.
 *
 * @param props Props of one layer, each with its tie key where the caller knows it (`withTieKeys`).
 * @param sizeOf Frame size lookup.
 * @param opaque Frame pixel opacity lookup.
 * @param tileSize Tile size in pixels.
 * @param wx World pixel x.
 * @param wy World pixel y.
 * @param pick The frame each prop is shown with and which props may be hit.
 * @returns The prop, or undefined when no opaque pixel of a pickable prop covers the point.
 */
export function propAt<P extends OrderedProp>(props: readonly P[], sizeOf: FrameSizeOf, opaque: OpaqueAt, tileSize: number, wx: number, wy: number, pick: PropPick = {}): P | undefined {
  let best: P | undefined;
  let bestDepth = Number.NEGATIVE_INFINITY;
  let bestKey = Number.NEGATIVE_INFINITY;
  for (const [index, prop] of props.entries()) {
    if (pick.pickable !== undefined && !pick.pickable(prop)) {
      continue;
    }
    const frame = pick.frameOf?.(prop) ?? prop.frame;
    const size = sizeOf(frame.tileset, frame.tile);
    const left = prop.x * tileSize;
    const top = (prop.y + 1) * tileSize - size.h;
    const px = Math.floor(wx - left);
    const py = Math.floor(wy - top);
    if (px < 0 || py < 0 || px >= size.w || py >= size.h) {
      continue;
    }
    const depth = propSortRow(prop, frame === prop.frame ? size : sizeOf(prop.frame.tileset, prop.frame.tile), tileSize);
    const key = prop.order ?? index;
    if (depth < bestDepth || (depth === bestDepth && key < bestKey)) {
      continue;
    }
    if (opaque(frame.tileset, frame.tile, prop.flipX ? size.w - 1 - px : px, prop.flipY ? size.h - 1 - py : py)) {
      best = prop;
      bestDepth = depth;
      bestKey = key;
    }
  }
  return best;
}

/**
 * The row a prop sorts by, for ordering a stack of props under the pointer: its own frame's `propSortRow`, whatever
 * frame a state shows over it, since the map draws its depth from that frame.
 *
 * @param prop The prop.
 * @param sizeOf Frame size lookup.
 * @param tileSize Tile size in pixels.
 * @returns The sort row, fractional.
 */
export function propRowAt(prop: PropInstance, sizeOf: FrameSizeOf, tileSize: number): number {
  return propSortRow(prop, sizeOf(prop.frame.tileset, prop.frame.tile), tileSize);
}

/**
 * The anchor that centres a sprite's columns on the pointer position; whole cells in, whole cells out.
 *
 * @param cx Pointer x in tiles.
 * @param cy Pointer y in tiles, kept as the anchor row.
 * @param size Frame pixel size.
 * @param tileSize Tile size in pixels.
 * @returns The anchor cell.
 */
export function placementCell(cx: number, cy: number, size: FrameSize, tileSize: number): { x: number; y: number } {
  const columns = Math.max(1, Math.ceil(size.w / tileSize));
  return { x: cx - Math.floor((columns - 1) / 2), y: cy };
}
